import moment from 'moment';

import { DATE_DISPLAY_FORMAT, ENTITY_PREFIX } from '../../../../../constants';
import { formatEntityId } from '../../../../../helpers';
import {
  INTAKE_QUESTIONNAIRE_BASE_PATH,
  INTAKE_TAB_IDS,
} from '../../../../../pages/questionnaires/intake/constants';
import {
  ONBOARDING_QUESTIONNAIRE_BASE_PATH,
  ONBOARDING_TAB_IDS,
} from '../../../../../pages/questionnaires/onboarding/constants';
import {
  QUESTIONNAIRE_STATUS_LABELS,
  QUESTIONNAIRE_TYPE_LABELS,
  QUESTIONNAIRE_TYPES,
} from './constants';

import type { FAutocompleteOption } from '../../../../../components/baseV2';
import type { IIntakeQuestionnaireBasic } from '../../../../../store/questionnaires/intake';
import type { IOnboardingQuestionnaireBasic } from '../../../../../store/questionnaires/onboarding';
import type { ProviderQuestionnaireTableRow } from './types';

export function formatQuestionnaireDate(
  date: string | null | undefined,
  utcOffset: number | null | undefined,
) {
  if (!date) return 'No date yet';

  return moment(date)
    .utcOffset((utcOffset ?? 0) / 60)
    .format(DATE_DISPLAY_FORMAT);
}

export function questionnaireDateSortCompare(order: 'asc' | 'desc') {
  return (obj1: { data: string }, obj2: { data: string }) => {
    const aDate = moment(obj1.data, DATE_DISPLAY_FORMAT, true);
    const bDate = moment(obj2.data, DATE_DISPLAY_FORMAT, true);

    if (!aDate.isValid() && !bDate.isValid()) return 0;
    if (!aDate.isValid()) return 1;
    if (!bDate.isValid()) return -1;

    return (aDate.valueOf() - bDate.valueOf()) * (order === 'asc' ? 1 : -1);
  };
}

function getQuestionnairePath(type: string, id: number) {
  if (type === QUESTIONNAIRE_TYPES.ONBOARDING) {
    return `${ONBOARDING_QUESTIONNAIRE_BASE_PATH}/${id}/${ONBOARDING_TAB_IDS.INTRODUCTION}`;
  }

  return `${INTAKE_QUESTIONNAIRE_BASE_PATH}/${id}/${INTAKE_TAB_IDS.INTRODUCTION}`;
}

export function toQuestionnaireTableRows(
  onboardingQuestionnaires: IOnboardingQuestionnaireBasic[],
  intakeQuestionnaires: IIntakeQuestionnaireBasic[],
): ProviderQuestionnaireTableRow[] {
  const onboardingRows = onboardingQuestionnaires.map((questionnaire) => ({
    type: QUESTIONNAIRE_TYPE_LABELS[QUESTIONNAIRE_TYPES.ONBOARDING],
    client: `${questionnaire.client.first_name} ${questionnaire.client.last_name}`,
    status: QUESTIONNAIRE_STATUS_LABELS[questionnaire.status],
    date_sent: formatQuestionnaireDate(questionnaire.date_sent, questionnaire.date_sent_utc_offset),
    date_concluded: formatQuestionnaireDate(
      questionnaire.date_concluded,
      questionnaire.date_concluded_utc_offset,
    ),
    questionnaire_id: formatEntityId(questionnaire.id, ENTITY_PREFIX.OnboardingQuestionnaire),
    path: getQuestionnairePath(QUESTIONNAIRE_TYPES.ONBOARDING, questionnaire.id),
  }));

  const intakeRows = intakeQuestionnaires.map((questionnaire) => ({
    type: QUESTIONNAIRE_TYPE_LABELS[QUESTIONNAIRE_TYPES.INTAKE],
    client: `${questionnaire.client.first_name} ${questionnaire.client.last_name}`,
    status: QUESTIONNAIRE_STATUS_LABELS[questionnaire.status],
    date_sent: formatQuestionnaireDate(questionnaire.date_sent, questionnaire.date_sent_utc_offset),
    date_concluded: formatQuestionnaireDate(
      questionnaire.date_concluded,
      questionnaire.date_concluded_utc_offset,
    ),
    questionnaire_id: formatEntityId(questionnaire.id, ENTITY_PREFIX.IntakeQuestionnaire),
    path: getQuestionnairePath(QUESTIONNAIRE_TYPES.INTAKE, questionnaire.id),
  }));

  return [...onboardingRows, ...intakeRows];
}

export function toOnboardingQuestionnaireDocumentOptions(
  onboardingQuestionnaires: IOnboardingQuestionnaireBasic[],
): FAutocompleteOption[] {
  return onboardingQuestionnaires.map((questionnaire) => ({
    value: String(questionnaire.id),
    label: `${questionnaire.client.first_name} ${questionnaire.client.last_name} (${formatEntityId(
      questionnaire.id,
      ENTITY_PREFIX.OnboardingQuestionnaire,
    )}) · ${QUESTIONNAIRE_STATUS_LABELS[questionnaire.status]}`,
  }));
}

export function getDocumentOptionLabel(
  options: ReadonlyArray<{ value: string; label: string }>,
  value: string | null,
) {
  if (!value) return '—';
  return options.find((option) => option.value === value)?.label ?? value;
}
